import { WaitForQueries } from '@graphcommerce/ecommerce-ui'
import { ApolloCartErrorAlert, useCartQuery } from '@graphcommerce/magento-cart'
import { CartPageDocument } from '@graphcommerce/magento-cart-checkout'
import { useConfigurableContext } from '@graphcommerce/magento-product-configurable'
import { ConfigurableCartItemFragment } from '@graphcommerce/magento-product-configurable/ConfigurableCartItem/ConfigurableCartItem.gql'
import { Money } from '@graphcommerce/magento-store'
import { FullPageMessage } from '@graphcommerce/next-ui'
import { i18n } from '@lingui/core'
import { Trans } from '@lingui/react'
import { CircularProgress } from '@mui/material'
import Link from 'next/link'
import { useMemo } from 'react'
import { RelatedProductGroups } from '../Amasty/RelatedProductGroups'
import { TertiaryButton } from '../Button/ButtonStyles'
import { CheckoutButton } from '../Cart/CheckoutButton/CheckoutButton'
import { EmptyCart } from '../Cart/EmptyCart/EmptyCart'
import { CartItem } from '../CartItems/CartItem/CartItem'
import { CartItems } from '../CartItems/CartItems/CartItems'
import { Icon } from '../Layout/Icon'
import { ConfigurableMinicartItem } from '../ProductConfigurable/ConfigurableCartItem/ConfigurableMinicartItem'

type MinicartProps = {
  onClose: () => unknown
  sku: string
  itemIds?: string[] | null
}

function AddedToCart(props: MinicartProps) {
  const { onClose, sku, itemIds } = props
  const { options, selection, select, getVariants } = useConfigurableContext(sku)
  const cart = useCartQuery(CartPageDocument, { returnPartialData: true, errorPolicy: 'all' })
  const { data, error } = cart
  const hasItems =
    (data?.cart?.total_quantity ?? 0) > 0 &&
    typeof data?.cart?.prices?.grand_total?.value !== 'undefined'

  const addedItem = useMemo(
    () =>
      data?.cart?.items?.find((item) => {
        const confItem = item as unknown as ConfigurableCartItemFragment
        if (itemIds && item && item.uid) {
          return itemIds.includes(item?.uid)
        }
        return confItem.configurable_options
          ? confItem.configurable_options.every((option) => {
              const optionDetails = options?.find(
                (o) => o?.uid === option?.configurable_product_option_uid,
              )
              return (
                optionDetails?.attribute_code &&
                option?.configurable_product_option_value_uid &&
                option?.configurable_product_option_value_uid ===
                  selection[optionDetails?.attribute_code]?.toString()
              )
            })
          : false
      }),
    [data, options, selection, itemIds],
  )

  const amountOfCartItems =
    data?.cart?.items && data?.cart?.items?.length > 0 ? data?.cart?.items?.length : '0'

  return (
    <WaitForQueries
      waitFor={cart}
      fallback={
        <FullPageMessage icon={<CircularProgress />} title='Loading'>
          <Trans id='This may take a second' />
        </FullPageMessage>
      }
    >
      <div className=' bg-pure-white'>
        {hasItems ? (
          <div>
            <div className='sticky top-0 z-50 flex items-center justify-between bg-purple-mist px-[30px] py-[18px]'>
              <span className='Type-XXXL-Bold flex items-center gap-[20px]'>
                <Icon name='check_circle' className='text-turquoise' />
                <Trans id='Added to cart' />
                <span className='Type-XXL-Bold text-70-grey'>({amountOfCartItems})</span>
              </span>
              <button
                type='button'
                className='flex cursor-pointer items-center justify-center'
                onClick={() => onClose()}
              >
                <Icon name='disabled_by_default' className='text-[32px] text-tight-black' />
              </button>
            </div>
            <div className='px-[40px]'>
              <CartItems
                containerClass='added-to-cart-items-list border-b-[1px] border-dashed border-b-50-grey last:border-b-0'
                items={addedItem ? [addedItem] : []}
                id={data?.cart?.id ?? ''}
                key='cart'
                renderer={{
                  BundleCartItem: CartItem,
                  ConfigurableCartItem: ConfigurableMinicartItem,
                  DownloadableCartItem: CartItem,
                  SimpleCartItem: CartItem,
                  VirtualCartItem: CartItem,
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore GiftCardProduct is only available in Commerce
                  GiftCardCartItem: CartItem,
                }}
              />
            </div>
            <div className='flex flex-col gap-4 border-t-[1px] border-50-grey p-[40px]'>
              {!!data?.cart?.prices?.setup_fees_total?.value && (
                <div className='mx-10 flex w-full  max-w-[70%] items-center justify-between'>
                  <span className='Type-XL-Regular text-dark-gray'>
                    <Trans id='Setup cost' />
                  </span>
                  <span className='Type-XL-Regular'>
                    <Money
                      value={data?.cart?.prices?.setup_fees_total?.value ?? 0}
                      currency={data?.cart?.prices?.subtotal_including_tax?.currency}
                    />
                  </span>
                </div>
              )}
              <div className='mx-10 flex w-full  max-w-[70%] items-center justify-between'>
                <span className='Type-XXL-Regular text-dark-gray'>
                  <Trans id='Cart total' />
                </span>
                <span className='Type-XXL-Regular'>
                  <Money {...data?.cart?.prices?.subtotal_excluding_tax_and_setup} />
                </span>
              </div>
              <div className='mt-[20px] grid gap-[14px]'>
                <CheckoutButton />
                <Link href='/cart'>
                  <TertiaryButton
                    type='button'
                    className='w-full bg-[#F2F2F2] uppercase'
                    color='secondary'
                    variant='contained'
                    size='small'
                  >
                    {i18n._(/* i18n */ 'View Cart')}
                  </TertiaryButton>
                </Link>
              </div>
            </div>
            <ApolloCartErrorAlert error={error} />
            {hasItems && addedItem && (
              <RelatedProductGroups position='cart_after_crosssel' uid={addedItem.product.uid} />
            )}
          </div>
        ) : (
          <EmptyCart displayOnMiniCart onClose={onClose}>
            {error && <ApolloCartErrorAlert error={error} />}
          </EmptyCart>
        )}
      </div>
    </WaitForQueries>
  )
}

export { AddedToCart }
